import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  HStack,
  VStack,
  Link,
  Button,
  Text,
  Spinner,
  BoxProps,
  TextProps,
  Tag,
  Stack,
} from "@chakra-ui/react";
import { KYDFullContact } from "common/interfaces/KYDCampaign";
import { useState, useEffect, useCallback } from "react";
import actions from "src/actions";
import { useParams } from "react-router-dom";
import StatCard from "../../Components/StatCard";
import { formatCurrency } from "src/Manager/EventDetail/Nav/Reporting/SettlementReportSection";
import dayjs from "dayjs";
import PurchaseHistoryTable from "./PurchaseHistoryTable";
import { genderToText } from "common/services/util/campaigns";
import { FiEdit, FiPlus } from "react-icons/fi";
import { useDialogWithOptions } from "src/Common/Dialog";
import EditTagsDialog, {
  EditTagsDialogContext,
  withEditTagsDialog,
} from "./EditTagsDialog";
import { Link as RouterLink } from "react-router-dom";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const InfoText = ({
  title,
  description,
  ...props
}: {
  title: string;
  description?: string;
} & TextProps) => {
  return (
    <Text
      color="#222"
      fontFamily="Work Sans"
      fontSize="16px"
      fontStyle="normal"
      fontWeight="400"
      lineHeight="140%"
      {...props}
    >
      {title}
      {description && (
        <Text
          as="span"
          color="rgba(34, 34, 34, 0.60)"
          fontSize="16px"
          fontWeight="400"
          lineHeight="140%"
        >
          {" "}
          {description}
        </Text>
      )}
    </Text>
  );
};

const Card = ({
  title,
  children,
  ...props
}: {
  title: string;
  children: React.ReactNode;
} & BoxProps) => {
  return (
    <VStack
      alignSelf="stretch"
      alignItems="flex-start"
      w="100%"
      minHeight="154px"
      borderRadius="10px"
      border="1px solid #E6E6E6"
      background="#FFFFFF"
      boxShadow="0px 10px 15px -5px rgba(247, 247, 247, 0.90)"
      px="16px"
      py="12px"
      h="186px"
      {...props}
    >
      <Text
        color="rgba(26, 32, 44, 0.75)"
        fontSize="16px"
        fontWeight="500"
        lineHeight="150%"
        letterSpacing="-0.48px"
        mb="6px"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
};

const ContactDetail = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [contact, setContact] = useState<KYDFullContact | null>();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const dialog = useDialogWithOptions(EditTagsDialogContext);

  const updateContact = useCallback(
    async (data: Partial<KYDFullContact>) => {
      try {
        await actions.ADMIN.updateContact(data);
      } catch (error) {
        console.error("Error updating contact:", error);
        throw error;
      }
    },
    [setContact]
  );

  const fetchContact = useCallback(async () => {
    setLoading(true);
    const contact = await actions.ADMIN.fetchContact(id);
    setLoading(false);
    setContact(contact);
  }, [id]);

  const openAddTagDialog = useCallback(() => {
    dialog.open();
  }, [dialog]);

  const handleSaveTags = useCallback(
    async (tags: string[]) => {
      await updateContact({
        ...contact,
        tags,
      });
      fetchContact();
      dialog.close();
    },
    [contact, updateContact, dialog]
  );

  useEffect(() => {
    fetchContact();
  }, [id]);

  if (loading || !contact) return <Spinner />;
  return (
    <VStack gap="24px">
      <ShowIfHasScope
        permissions={permissions}
        checkFor={["crm/contacts:write", "crm/write"]}
      >
        <EditTagsDialog
          initialValue={contact.tags}
          onSave={handleSaveTags}
          onClose={() => {
            dialog.close();
          }}
        />
      </ShowIfHasScope>
      <HStack w="100%" h="100%" justifyContent="space-between">
        <VStack alignItems="flex-start">
          <Link as={RouterLink} to="/campaigns/contacts">
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
            >
              Contacts
            </Button>
          </Link>
          <Text
            fontSize="24px"
            color="#1A202C"
            fontWeight="500"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight="30px"
            letterSpacing="-0.72px"
          >
            {contact.first_name} {contact.last_name} Profile
          </Text>
        </VStack>
      </HStack>
      <Stack
        direction={{ base: "column", md: "row" }}
        w="100%"
        h="100%"
        justifyContent="space-between"
        flexWrap={"wrap"}
        gap={"24px"}
        mt="16px"
      >
        <StatCard
          flex={1}
          title="Total Spent"
          value={formatCurrency(
            Math.round(contact.total_spent.amount),
            contact.total_spent.currency as Dinero.Currency
          )}
        />
        <StatCard
          flex={1}
          title="Avg. Order Value"
          value={formatCurrency(
            Math.round(contact.average_order_value.amount),
            contact.average_order_value.currency as Dinero.Currency
          )}
        />
        <StatCard
          flex={1}
          title="Avg. Tickets Per Order"
          value={contact.average_items_per_order.toFixed(1)}
        />
        <StatCard
          flex={1}
          title="Last Active"
          value={dayjs(contact.last_active_at).fromNow()}
          info={dayjs(contact.last_active_at).format("MMM D, 'YY")}
        />
      </Stack>
      <Stack w="100%" gap="24px" direction={{ base: "column", md: "row" }}>
        <Card title="Info">
          {contact.email && <InfoText title={`Email: ${contact.email}`} />}
          {contact.phone_number && (
            <InfoText title={`Phone: ${contact.phone_number}`} />
          )}
          {(contact.gender || contact.age) && (
            <InfoText
              title={`Gender: ${contact.gender ? genderToText(contact.gender) : ""} ${contact.age ? contact.age : ""}`}
            />
          )}
        </Card>
        <Card title="Campaign Engagement">
          <InfoText
            title={
              contact.unsubscribed_at
                ? "Inactive Subscriber"
                : "Active Subscriber"
            }
          />
          <InfoText
            title={`${contact.total_emails_received || 0} emails received`}
          />
          <InfoText
            title={`${contact.total_emails_opened || 0} emails opened`}
          />
          <InfoText
            title={`${contact.total_emails_clicked || 0} emails clicked`}
          />
        </Card>
        <Card title="Tags">
          <HStack flexWrap={"wrap"} gap={"8px"}>
            {contact.tags.map((tag) => (
              <Tag
                key={tag}
                sx={{
                  flexShrink: 0,
                  color: "rgba(34, 34, 34, 0.80)",
                  fontFamily: "Work Sans",
                  fontSize: "14px",
                }}
              >
                {tag}
              </Tag>
            ))}
            <ShowIfHasScope
              permissions={permissions}
              checkFor={["crm/contacts:write", "crm/write"]}
            >
              <Tag
                onClick={openAddTagDialog}
                sx={{
                  cursor: "pointer",
                  color: "rgba(34, 34, 34, 0.80)",
                  fontFamily: "Work Sans",
                  fontSize: "14px",
                }}
              >
                <HStack gap={"4px"}>
                  <FiEdit />
                  <Text>Edit Tags</Text>
                </HStack>
              </Tag>
            </ShowIfHasScope>
          </HStack>
        </Card>
      </Stack>
      <VStack alignItems={"flex-start"} w={"100%"} mt="24px">
        <Text fontSize="24px" fontWeight="600" mb={"12px"}>
          Purchase History
        </Text>
        {contact.purchase_history?.length > 0 ? (
          <PurchaseHistoryTable data={contact.purchase_history || []} />
        ) : (
          <Text>No purchase history</Text>
        )}
      </VStack>
    </VStack>
  );
};

export default withEditTagsDialog(ContactDetail);
